/**
 * Error logging — capture, group, and deliver.
 *
 * What this replaced was a reporting path that could not report. Four things were wrong with it at
 * once, and each one on its own was enough to make the whole thing silent:
 *
 *  1. It POSTed to `https://hl.tomake.design/log-errors` — a host this repository does not serve
 *     and cannot verify. `apiConfig.baseUrl` is `<same origin>/api`; that host is something else
 *     entirely, and no route anywhere in `functions/` answers `/log-errors`.
 *  2. It flattened every argument with `String(arg)`, so `logger.error("Import failed:", err)`
 *     sent the text `Error: boom` and threw the stack away. The one thing that makes an error
 *     report actionable was the thing it dropped.
 *  3. In the plugin sandbox there is no `fetch` at all — Figma only permits network access from
 *     the UI iframe. Every `mainLogger.error(...)` in code.ts therefore queued a report that
 *     could never be sent, and the failure was swallowed by the transport's own catch.
 *  4. Nothing installed handlers for uncaught exceptions or unhandled promise rejections, so the
 *     errors nobody wrote a try/catch for — the ones you most need to see — were never captured.
 *
 * The shape now:
 *
 *   - A **transport** is injected rather than hardcoded. The UI installs one that POSTs to this
 *     app's own API; the sandbox installs one that forwards over the existing plugin message
 *     channel, so sandbox errors reach the same queue by the only route out of the sandbox.
 *     Until a transport is set, reports queue rather than being thrown away.
 *   - Errors are **serialized**, not stringified: name, message and stack survive.
 *   - Identical errors are **grouped by fingerprint** with a count, so a failure inside a loop
 *     sends one report saying ×2000 instead of flooding the queue and evicting everything else.
 *   - The queue is **bounded**, and what it drops it counts, so a truncated batch says so.
 *   - Delivery is retried once, and a transport that never works says so once.
 */

export enum LogLevel {
  ERROR = 'ERROR',
  INFO = 'INFO',
  DEBUG = 'DEBUG',
  WARN = 'WARN',
}

/** One grouped error, as it goes over the wire. */
export interface ErrorReport {
  fingerprint: string;
  level: string;
  tag: string;
  message: string;
  stack?: string;
  /** How many identical occurrences this row represents. */
  count: number;
  firstSeen: number;
  lastSeen: number;
  /** Extra arguments the caller passed, JSON-safe. */
  context?: string;
}

export interface ErrorBatch {
  sessionId: string;
  userId: string | null;
  buildId: string | null;
  /** 'ui' or the sandbox's editor type — tells you where the error happened. */
  source: string;
  /** Reports dropped because the queue was full. Non-zero means this batch is incomplete. */
  dropped: number;
  errors: ErrorReport[];
}

export type ErrorTransport = (batch: ErrorBatch) => void | Promise<void>;

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

const FLUSH_INTERVAL_MS = 5000;
/** Distinct fingerprints held before new ones are dropped. Repeats of a known one are free. */
const MAX_QUEUE_SIZE = 50;
/** Truncation limits, so one enormous payload cannot fill the queue or the request. */
const MAX_MESSAGE_CHARS = 2000;
const MAX_STACK_CHARS = 8000;
const MAX_CONTEXT_CHARS = 4000;

let transport: ErrorTransport | null = null;
let currentUserId: string | null = null;
let buildId: string | null = null;
let source = 'ui';
let consoleOn = false;

/**
 * Identifies one plugin run, so reports from the same session can be read together without
 * needing a user id — which is absent until the sandbox answers with the Figma user.
 */
const sessionId = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;

const queue = new Map<string, ErrorReport>();
let dropped = 0;
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let transportFailureReported = false;

/**
 * Point the logger at somewhere reports can actually go.
 *
 * Called once per side: `main.tsx` installs the HTTP transport, `plugin/code.ts` installs the
 * forward-to-UI one. Anything logged before this queues and is delivered on the first flush.
 */
export function configureErrorReporting(options: {
  transport: ErrorTransport;
  userId?: string | null;
  buildId?: string | null;
  source?: string;
  console?: boolean;
}): void {
  transport = options.transport;
  if (options.userId !== undefined) currentUserId = options.userId;
  if (options.buildId !== undefined) buildId = options.buildId;
  if (options.source) source = options.source;
  if (options.console !== undefined) consoleOn = options.console;
  if (queue.size > 0) void flushErrorQueue();
}

/** Attribute subsequent reports to a user. Called once the sandbox answers with the Figma user. */
export function setErrorReportingUserId(userId: string): void {
  currentUserId = userId;
}

/** Turn console output on or off at runtime. Off by default; the transport is the real channel. */
export function setConsoleLogging(enabled: boolean): void {
  consoleOn = enabled;
}

function consoleEnabled(): boolean {
  if (consoleOn) return true;
  try {
    return !!(globalThis as { process?: { env?: Record<string, string> } }).process?.env?.TOFIG_LOG;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Serialization
// ---------------------------------------------------------------------------

function truncate(value: string, max: number): string {
  return value.length <= max ? value : `${value.slice(0, max)}… [${value.length - max} more chars]`;
}

/**
 * Turn one logged argument into text, keeping what makes an error useful.
 *
 * `String(err)` yields "Error: boom" and loses the stack; `JSON.stringify(err)` yields `{}`,
 * because an Error's own properties are not enumerable. Both were in the old implementation.
 */
function describe(arg: unknown): { text: string; stack?: string } {
  if (arg instanceof Error) {
    return { text: `${arg.name}: ${arg.message}`, stack: arg.stack };
  }
  if (typeof arg === 'string') return { text: arg };
  if (arg === null) return { text: 'null' };
  if (arg === undefined) return { text: 'undefined' };
  if (typeof arg === 'object') {
    // An object shaped like an error — what a rejected fetch or a postMessage payload carries.
    const maybe = arg as { message?: unknown; stack?: unknown; name?: unknown };
    if (typeof maybe.message === 'string') {
      return {
        text: `${typeof maybe.name === 'string' ? maybe.name : 'Error'}: ${maybe.message}`,
        stack: typeof maybe.stack === 'string' ? maybe.stack : undefined,
      };
    }
    try {
      return { text: JSON.stringify(arg) ?? String(arg) };
    } catch {
      // Circular, or a value JSON refuses. Neither should cost us the report.
      return { text: '[unserializable object]' };
    }
  }
  return { text: String(arg) };
}

/**
 * A stable key for "the same error happening again".
 *
 * Digits are collapsed so that ids, offsets and timestamps inside a message do not split one
 * recurring failure into a thousand distinct ones — which is what would otherwise evict every
 * other report from a bounded queue.
 */
function fingerprintOf(level: string, tag: string, message: string): string {
  return `${level}|${tag}|${message.replace(/\d+/g, '#').slice(0, 200)}`;
}

// ---------------------------------------------------------------------------
// Queue
// ---------------------------------------------------------------------------

function record(level: string, tag: string, args: unknown[]): void {
  const parts = args.map(describe);
  const message = truncate(parts.map((p) => p.text).join(' '), MAX_MESSAGE_CHARS);
  const stack = parts.find((p) => p.stack)?.stack;
  const fingerprint = fingerprintOf(level, tag, message);
  const now = Date.now();

  const existing = queue.get(fingerprint);
  if (existing) {
    // Repeats are free: the count goes up and the queue does not grow.
    existing.count++;
    existing.lastSeen = now;
    return;
  }

  if (queue.size >= MAX_QUEUE_SIZE) {
    dropped++;
    return;
  }

  const context = args.length > 1 ? args.slice(1) : undefined;
  let serializedContext: string | undefined;
  if (context) {
    try {
      serializedContext = truncate(JSON.stringify(context, jsonSafe), MAX_CONTEXT_CHARS);
    } catch {
      serializedContext = undefined;
    }
  }

  queue.set(fingerprint, {
    fingerprint,
    level,
    tag,
    message,
    stack: stack ? truncate(stack, MAX_STACK_CHARS) : undefined,
    count: 1,
    firstSeen: now,
    lastSeen: now,
    context: serializedContext,
  });

  if (queue.size >= MAX_QUEUE_SIZE) {
    void flushErrorQueue();
  } else if (!flushTimer) {
    flushTimer = setTimeout(() => {
      flushTimer = null;
      void flushErrorQueue();
    }, FLUSH_INTERVAL_MS);
  }
}

/** Errors are not JSON-serializable by default; make them so wherever they appear in context. */
function jsonSafe(_key: string, value: unknown): unknown {
  if (value instanceof Error) return { name: value.name, message: value.message, stack: value.stack };
  return value;
}

/** Send whatever has accumulated. Safe to call at any time; a no-op when there is nothing. */
export async function flushErrorQueue(): Promise<void> {
  if (queue.size === 0) return;
  if (!transport) return; // keep queuing until someone can deliver

  const batch: ErrorBatch = {
    sessionId,
    userId: currentUserId,
    buildId,
    source,
    dropped,
    errors: [...queue.values()],
  };
  queue.clear();
  dropped = 0;

  try {
    await transport(batch);
  } catch (error) {
    // Deliberately NOT re-queued: retrying a failing transport from inside the error path is how
    // a loop starts. But it is not invisible either — a transport that never works used to look
    // exactly like one that always did.
    if (!transportFailureReported) {
      transportFailureReported = true;
      if (consoleEnabled()) console.error('[logger] error reporting transport failed:', error);
    }
  }
}

/**
 * Deliver a batch that was produced somewhere else — in practice, the plugin sandbox.
 *
 * The sandbox has no `fetch`, so its transport forwards over the plugin message channel and the
 * UI hands the result here. The batch keeps its OWN sessionId and source, so a sandbox error is
 * still identifiable as one rather than being relabelled as a UI error; only the user id is
 * filled in, because the sandbox is where the Figma user is known but the reports are usually
 * queued before it answers.
 */
export function forwardErrorBatch(batch: ErrorBatch): void {
  if (!batch || !Array.isArray(batch.errors) || batch.errors.length === 0) return;
  const outgoing: ErrorBatch = {
    ...batch,
    userId: batch.userId ?? currentUserId,
    buildId: batch.buildId ?? buildId,
  };
  if (!transport) {
    // No transport yet — fold the reports into the local queue so they are not lost, rather than
    // dropping them on the floor the way the previous implementation would have.
    for (const report of outgoing.errors) {
      const existing = queue.get(report.fingerprint);
      if (existing) {
        existing.count += report.count;
        existing.lastSeen = Math.max(existing.lastSeen, report.lastSeen);
      } else if (queue.size < MAX_QUEUE_SIZE) {
        queue.set(report.fingerprint, { ...report });
      } else {
        dropped++;
      }
    }
    return;
  }
  void Promise.resolve(transport(outgoing)).catch((error) => {
    if (!transportFailureReported) {
      transportFailureReported = true;
      if (consoleEnabled()) console.error('[logger] forwarding sandbox errors failed:', error);
    }
  });
}

/** Flush on the way out. Kept as the historical name because App.tsx calls it on unmount. */
export function flushErrors(): Promise<void> {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  return flushErrorQueue();
}

// ---------------------------------------------------------------------------
// Global capture
// ---------------------------------------------------------------------------

let globalHandlersInstalled = false;

/**
 * Capture what no try/catch caught.
 *
 * Nothing did this before, so a render that threw, or a promise nobody awaited, produced no report
 * at all — while the handled errors, which someone had already thought about, were the only ones
 * being collected.
 */
export function installGlobalErrorHandlers(target: {
  addEventListener: (type: string, listener: (event: any) => void) => void;
}): () => void {
  if (globalHandlersInstalled) return () => {};
  globalHandlersInstalled = true;
  const log = getLogger('Uncaught');

  const onError = (event: any) => {
    log.error(event?.error ?? event?.message ?? 'Unknown error', {
      filename: event?.filename,
      lineno: event?.lineno,
      colno: event?.colno,
    });
  };
  const onRejection = (event: any) => {
    log.error(event?.reason ?? 'Unhandled promise rejection');
  };

  target.addEventListener('error', onError);
  target.addEventListener('unhandledrejection', onRejection);

  return () => {
    globalHandlersInstalled = false;
  };
}

// ---------------------------------------------------------------------------
// Logger
// ---------------------------------------------------------------------------

export class Logger {
  private tag: string;

  constructor(tag: string) {
    this.tag = tag;
  }

  static getLogger(tag: string): Logger {
    return new Logger(tag);
  }

  error(...args: unknown[]): void {
    if (consoleEnabled()) console.error(`[${this.tag}]`, ...args);
    record(LogLevel.ERROR, this.tag, args);
  }

  warn(...args: unknown[]): void {
    if (consoleEnabled()) console.warn(`[${this.tag}]`, ...args);
    record(LogLevel.WARN, this.tag, args);
  }

  info(...args: unknown[]): void {
    if (consoleEnabled()) console.info(`[${this.tag}]`, ...args);
  }

  debug(...args: unknown[]): void {
    if (consoleEnabled()) console.debug(`[${this.tag}]`, ...args);
  }
}

export function getLogger(tag: string): Logger {
  return Logger.getLogger(tag);
}

/** Test seam: drop everything queued and forget the configured transport. */
export function __resetErrorReportingForTests(): void {
  queue.clear();
  dropped = 0;
  transport = null;
  currentUserId = null;
  buildId = null;
  source = 'ui';
  transportFailureReported = false;
  globalHandlersInstalled = false;
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
}

/** Test seam: what is queued right now, without sending it. */
export function __peekErrorQueueForTests(): { errors: ErrorReport[]; dropped: number } {
  return { errors: [...queue.values()], dropped };
}
